import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { createVerifiedCheckoutSession } from "../../VerifyAccount/api";
import {
  checkoutErrorMessage,
  resolveBilling,
  resolveCompanyId,
  resolveCompanyName,
  shouldBail,
} from "./membershipFields";

/* The membership checkout, transcribed from PaymentScreen.jsx. */
export const useMembershipCheckout = ({ lead, token, onSkip, companyId } = {}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const companyID = resolveCompanyId({
    companyIdProp: companyId,
    stateCompanyId: location.state?.currentCompanyId,
  });
  const companyName = resolveCompanyName({
    stateName: location.state?.companyName,
    lead,
    token,
    companyID,
  });

  const [billing, setBilling] = useState(() => resolveBilling(location.state));
  const [stage, setStage] = useState("pick");
  const [clientSecret, setClientSecret] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  /* Transcribed: with neither an id nor a name there is nothing to enrol. */
  useEffect(() => {
    if (shouldBail({ companyID, companyName })) navigate("/");
  }, [companyID, companyName, navigate]);

  const pay = async () => {
    /* Guarded twice over: a second click while the first is in flight would
       open a second checkout session, and clicking Pay from the 'paying'
       stage would throw the first one away. */
    if (submitting || stage !== "pick") return;
    setSubmitting(true);
    setError(null);
    try {
      const data = await createVerifiedCheckoutSession({
        companyID,
        token,
        plan: billing,
      });
      if (!data?.client_secret) throw new Error("No client_secret returned");
      setClientSecret(data.client_secret);
      setStage("paying");
    } catch (e) {
      setError(checkoutErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  /* Going back to the cards throws the session away: its plan is baked in, so
     reusing it after changing the cycle would charge the old one. */
  const changePlan = () => {
    setStage("pick");
    setClientSecret(null);
    setError(null);
  };

  const skip = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onSkip?.();
    } catch (e) {
      /* Transcribed: the flag is only released on failure. On success the
         caller navigates away, and re-enabling the buttons on a screen that is
         leaving would let a second skip fire. */
      setSubmitting(false);
    }
  };

  return {
    companyID,
    companyName,
    billing,
    setBilling,
    stage,
    clientSecret,
    submitting,
    error,
    pay,
    changePlan,
    skip,
  };
};

export default useMembershipCheckout;
